import { useCallback, useEffect, useRef, useState } from "react";
import { Position } from "@xyflow/react";
import { getIntegrationDetails } from "../../../api/routes/routesApi";
import { AccountBundle, ArrowState, FlowDirectionKey } from "./flowMapTypes";

export type FlowArrow = {
    id: string;                    // `${destinationId}-out` | `${destinationId}-in`
    destinationId: string;
    direction: FlowDirectionKey;
    fromId: string; toId: string;  // integration ids
    state: ArrowState;
    reason?: string;               // why not enableable, for the panel
};

export const hasType = (actions: any[], type: string) => (actions || []).some((a) => a.type === type);

const toBundle = (raw: any, providerId: string): AccountBundle => ({
    id: raw.id,
    name: raw.name,
    base_url: raw.base_url,
    status: raw.status,
    owner: raw.owner,
    techName: raw.type?.name || "",
    techValue: raw.type?.value || "",
    actions: (raw.type?.actions || []),
    configurations: raw.configurations || [],
    isProvider: raw.id === providerId,
});

// One getIntegrationDetails per account, in parallel. `reload(id)` re-fetches a
// single account (all of them when omitted) — used after saves and after a
// direction is enabled. `seed` is an already-loaded integration (the parent
// fetches the provider itself) — it's used directly instead of re-fetched.
export function useAccountBundles(token: string | undefined, ids: string[], providerId: string, seed?: any): {
    bundles: Record<string, AccountBundle>;
    loading: boolean;
    // True when at least one account could not be loaded — the caller surfaces it
    // instead of silently rendering a graph built from missing capabilities.
    failed: boolean;
    reload: (id?: string) => Promise<void>;
} {
    const [bundles, setBundles] = useState<Record<string, AccountBundle>>({});
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const idsKey = ids.join(",");
    const idsRef = useRef<string[]>(ids);
    idsRef.current = ids;
    const seeded = seed?.id && seed?.type?.actions ? seed : undefined;

    useEffect(() => {
        if (!token || ids.length === 0) { setLoading(false); return; }
        let cancelled = false;
        (async () => {
            setLoading(true);
            // allSettled, not all: one failing account must not throw away the
            // others — a rejected Promise.all left every bundle missing, which the
            // graph reads as "this destination can't send data".
            const toFetch = ids.filter((id) => id !== seeded?.id);
            const settled = await Promise.allSettled(toFetch.map((id) => getIntegrationDetails(token, id)));
            if (cancelled) return;
            const map: Record<string, AccountBundle> = {};
            if (seeded) map[seeded.id] = toBundle(seeded, providerId);
            let anyFailed = false;
            settled.forEach((outcome) => {
                if (outcome.status === "fulfilled") {
                    const r: any = outcome.value;
                    if (r?.id) map[r.id] = toBundle(r, providerId);
                    else anyFailed = true;
                } else {
                    anyFailed = true;
                    console.error("failed to load account integration", outcome.reason);
                }
            });
            setBundles(map);
            setFailed(anyFailed);
            setLoading(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, idsKey, providerId, seeded]);

    const reload = useCallback(async (id?: string) => {
        if (!token) return;
        const targets = id ? [id] : idsRef.current;
        const settled = await Promise.allSettled(targets.map((t) => getIntegrationDetails(token, t)));
        let anyFailed = false;
        setBundles((prev) => {
            const next = { ...prev };
            settled.forEach((outcome) => {
                if (outcome.status === "fulfilled") {
                    const r: any = outcome.value;
                    if (r?.id) next[r.id] = toBundle(r, providerId);
                } else {
                    anyFailed = true;
                    console.error("failed to reload account integration", outcome.reason);
                }
            });
            return next;
        });
        setFailed(anyFailed);
    }, [token, providerId]);

    return { bundles, loading, failed, reload };
}

// Exactly two arrows per destination: the Route's own provider → destination
// route (always enabled), and the reverse destination → provider route, whose
// state depends on both ends' capabilities plus whether the route already exists.
export function buildFlowArrows(input: {
    providerId: string;
    provider: any;
    destinations: any[];
    reverseRoutesBySite: Record<string, string[]>;
    providerCanReceive: boolean;
    // The Route's own enabled switch — a disabled Route draws its outgoing
    // arrows as off, and the out-arrow panel is where it gets re-enabled.
    routeEnabled: boolean;
    // Derived from the destination's own actions (the bundles the tab already
    // fetches) — no separate action-type API query needed. `undefined` means the
    // destination's integration hasn't loaded (or failed to load): the direction
    // is drawn as unavailable, but without asserting a reason we don't know.
    destinationCanSend: (destId: string) => boolean | undefined;
    t: (key: string, options?: any) => string;
}): FlowArrow[] {
    const { providerId, provider, destinations, reverseRoutesBySite, providerCanReceive, routeEnabled, destinationCanSend, t } = input;
    const arrows: FlowArrow[] = [];

    destinations.forEach((dest) => {
        arrows.push({
            id: `${dest.id}-out`,
            destinationId: dest.id,
            direction: "out",
            fromId: providerId,
            toId: dest.id,
            state: routeEnabled ? "enabled" : "disabled",
        });

        const reverseOn = (reverseRoutesBySite[dest.id] || []).length > 0;
        const destCanSend = destinationCanSend(dest.id);
        let state: ArrowState = "not-enableable";
        let reason: string | undefined;
        if (reverseOn) state = "enabled";
        else if (destCanSend && providerCanReceive) state = "disabled";
        else if (destCanSend === undefined) reason = undefined;
        else if (!destCanSend) reason = t("{{name}} can't send data in this direction, so the configurations below are not in use.", { name: dest.name });
        else reason = t("{{name}} can't receive data, so the configurations below are not in use.", { name: provider?.name || t("The source") });

        arrows.push({
            id: `${dest.id}-in`,
            destinationId: dest.id,
            direction: "in",
            fromId: dest.id,
            toId: providerId,
            state,
            reason,
        });
    });

    return arrows;
}

// Deterministic hub-and-spoke layout: provider on the left, destinations stacked
// on the right, no auto-layout library.
// Node dimensions are declared, not measured. React Flow hides a node until its
// own ResizeObserver reports a size, and that observer goes stale for a node that
// survives a graph change (remove the only destination and the source node stayed
// hidden forever — a blank map). Both cards have a fixed size in CSS anyway, so
// telling React Flow the size up front skips measurement entirely: the nodes are
// visible on the first render and fitView has real bounds immediately. NODE_H must
// stay in step with the card's h-[100px] in AccountNode.
export const NODE_W = 260, NODE_H = 100, V_GAP = 40, H_GAP = 320;

// Selection is deliberately NOT an input: the structural graph stays memoized
// across clicks, and the caller layers `isSelected` onto nodes/edges per render.
export function buildGraph(input: {
    provider: any;
    destinations: any[];
    arrows: FlowArrow[];
    bundles: Record<string, AccountBundle>;
    destinationsWithRules: Set<string>;
    // Rules of the reverse Routes, keyed by the site that sends the data back.
    reverseDestinationsWithRules?: Set<string>;
    // Checklist state: accounts with required fields still unconfigured.
    needsSetupAccounts?: Set<string>;
    t: (key: string, options?: any) => string;
}) {
    const { provider, destinations, arrows, bundles, destinationsWithRules, reverseDestinationsWithRules, needsSetupAccounts, t } = input;

    const destY = (i: number) => i * (NODE_H + V_GAP);
    const providerY = ((Math.max(destinations.length, 1) - 1) * (NODE_H + V_GAP)) / 2;

    // A node's role isn't fixed: once a reverse route is enabled, the destination
    // is also sending and the provider is also receiving. The label lists the roles
    // the account actually plays in this Route, primary one first. The sending
    // side is the "provider", never the "source" — a source in Gundi is a device
    // discovered through the Route, not the system it came from.
    const anyReverseEnabled = arrows.some((a) => a.direction === "in" && a.state === "enabled");
    const roleLabel = (account: any, isProvider: boolean) => {
        if (isProvider) return anyReverseEnabled ? `${t("Provider")} · ${t("Destination")}` : t("Provider");
        const sendsBack = arrows.some((a) => a.destinationId === account.id && a.direction === "in" && a.state === "enabled");
        return sendsBack ? `${t("Destination")} · ${t("Provider")}` : t("Destination");
    };

    const nodeFor = (account: any, x: number, y: number, isProvider: boolean) => ({
        id: account.id,
        type: "account",
        position: { x, y },
        width: NODE_W,
        height: NODE_H,
        // Declared handle geometry, mirroring AccountNode's handles (35%/65% of
        // the fixed card height). Same reason as the declared width/height above:
        // React Flow refuses to draw an edge while either endpoint node is
        // unmeasured, and a node whose measurement is lost during a graph rebuild
        // (a dropped ResizeObserver delivery) never re-measures — its card never
        // changes size again, so every arrow through it vanished until a refresh.
        // Measured bounds still take precedence when present; these are the
        // fallback that keeps the arrows independent of measurement entirely.
        handles: isProvider ? [
            { id: "out-source", type: "source", position: Position.Right, x: NODE_W, y: NODE_H * 0.35, width: 0, height: 0 },
            { id: "in-target", type: "target", position: Position.Right, x: NODE_W, y: NODE_H * 0.65, width: 0, height: 0 },
        ] : [
            { id: "out-target", type: "target", position: Position.Left, x: 0, y: NODE_H * 0.35, width: 0, height: 0 },
            { id: "in-source", type: "source", position: Position.Left, x: 0, y: NODE_H * 0.65, width: 0, height: 0 },
        ],
        draggable: false,
        selectable: false,
        data: {
            name: bundles[account.id]?.name || account.name,
            base_url: bundles[account.id]?.base_url ?? account.base_url,
            status: bundles[account.id]?.status ?? account.status,
            techName: account.type?.name || "",
            techValue: account.type?.value || "",
            isProvider,
            roleLabel: roleLabel(account, isProvider),
            needsSetup: needsSetupAccounts?.has(account.id) ?? false,
            isSelected: false,
        },
    });

    const nodes = [
        nodeFor(provider, 0, providerY, true),
        ...destinations.map((d, i) => nodeFor(d, NODE_W + H_GAP, destY(i), false)),
    ];

    const edges = arrows.map((arrow) => ({
        id: arrow.id,
        source: arrow.fromId,
        target: arrow.toId,
        type: "direction",
        sourceHandle: arrow.direction === "out" ? "out-source" : "in-source",
        targetHandle: arrow.direction === "out" ? "out-target" : "in-target",
        focusable: false,
        selectable: false,
        data: {
            state: arrow.state,
            direction: arrow.direction,
            destinationId: arrow.destinationId,
            // Field mapping is configured per flow, so its badge rides on the
            // arrow rather than on the destination node — and each direction
            // reads the rules of its own route record.
            hasRules: arrow.direction === "out"
                ? destinationsWithRules.has(arrow.destinationId)
                : !!reverseDestinationsWithRules?.has(arrow.destinationId),
            isSelected: false,
        },
    }));

    return { nodes, edges };
}
